<template>
  <div class="profile" v-if="user">
    <h1 v-translate>Profile</h1>
    <md-button :disabled="saving" class="button" @click="handleSubmit" v-if="!loading"
      ><span class="inherit-font" v-translate>Save</span></md-button
    >
    <LoadSpinner v-if="saving || loading" />
    <form class="form" @submit.prevent="handleSubmit" v-if="!loading">
      <md-field class="md-invalid">
        <label for="first-name" v-translate>First name</label>
        <md-input
          tabindex="1"
          v-model="user.firstName"
          name="first-name"
          id="first-name"
          required
        ></md-input>
        <span class="profile__error" :class="['md-error', 'ruby-font']">
          {{ errors && errors.firstName ? errors.firstName[0] : '' }}
        </span>
      </md-field>
      <md-field class="md-invalid">
        <label for="last-name" v-translate>Last name</label>
        <md-input
          tabindex="2"
          v-model="user.lastName"
          name="last-name"
          id="last-name"
          required
        ></md-input>
        <span class="profile__error" :class="['md-error', 'ruby-font']">
          {{ errors && errors.lastName ? errors.lastName[0] : '' }}
        </span>
      </md-field>
      <md-field class="md-invalid">
        <label for="email-address" v-translate>Personal email address</label>
        <md-input
          tabindex="3"
          v-model="user.email"
          name="email-address"
          id="email-address"
          required
        ></md-input>
        <span class="profile__error" :class="['md-error', 'ruby-font']">
          {{ errors && errors.email ? errors.email[0] : '' }}
        </span>
      </md-field>
      <md-field class="md-invalid">
        <label for="email-address" v-translate>Re-enter personal email address</label>
        <md-input
          tabindex="3"
          v-model="user.confirmEmail"
          name="confirm-email"
          id="confirm-email"
          required
        ></md-input>
        <span class="profile__error" :class="['md-error', 'ruby-font']">
          {{ errors && errors.confirmEmail ? errors.confirmEmail[0] : '' }}
        </span>
      </md-field>
      <md-field class="md-invalid">
        <label for="paypal" v-translate>PayPal email address</label>
        <md-input tabindex="5" v-model="user.paypalEmail" name="paypal" id="paypal"></md-input>
        <span class="profile__error" :class="['md-error', 'ruby-font']">
          {{ errors && errors.paypalEmail ? errors.paypalEmail[0] : '' }}
        </span>
      </md-field>
      <md-field
        :class="{ 'md-invalid': this.errors || !this.zipCodeValid }"
      >
        <label v-translate>Zip code</label>
        <md-input
          tabindex="6"
          v-mask="zipCodeMask"
          v-model="user.zipCode"
          :required="employeeSchema.hasOwnProperty('zipCode')">
        </md-input>
        <span class="profile__error" :class="['md-error', 'ruby-font']">
          {{
            this.zipCodeValid
              ? ''
              : (errors.zipCode && errors.zipCode[0]) ||
                (clientErrors.zipCode && clientErrors.zipCode[0]) ||
                'Invalid'
          }}
        </span>
      </md-field>
      <div class="profile__section" v-if="enableLangPref">
        <label for="contact">
          <p class="subheading" v-translate>Language preferences</p>
        </label>
        <div>
          <span class="profile__subtitle italics" v-translate>
            What is your preferred language for Grant Circle communication?
          </span>
        </div>
        <md-checkbox v-model="user.preferredLanguage" value="en_US">
          <p v-translate>English</p>
        </md-checkbox>
        <md-checkbox v-model="user.preferredLanguage" value="es_ES"
          ><p v-translate>Spanish</p></md-checkbox
        >
        <div :class="{ 'md-invalid': this.errors }">
          <span class="profile__error" :class="['md-error', 'ruby-font']">
            {{ errors && errors.preferredLanguage ? errors.preferredLanguage[0] : '' }}
          </span>
        </div>
      </div>
      <div v-if="enableSms" class="profile__section">
        <label for="notifications">
          <p class="subheading" v-translate>Notification preferences</p>
        </label>
        <p class="profile__subtitle">
          <span class="profile__subtitle" v-translate
            >Do you prefer that we communicate with you via email, text message, or both? Select one
            or more below.</span
          >
          <span>&nbsp;</span>
          <span class="profile__subtitle italics" v-translate>(Messaging rates may apply)</span>
        </p>
        <md-checkbox v-model="user.enableEmailAlerts">
          <p v-translate>Email</p>
        </md-checkbox>
        <md-checkbox v-model="user.enableSmsAlerts">
          <p v-translate>SMS</p>
        </md-checkbox>
        <div :class="{ 'md-invalid': this.errors }">
          <span class="profile__error" :class="['md-error', 'ruby-font']">
            {{ errors && errors.notifPreferences ? errors.notifPreferences[0] : '' }}
          </span>
        </div>
      </div>
      <md-field v-if="enableSms" :class="{ 'md-invalid': this.errors || !this.phoneNumberValid }">
        <label for="phone" v-translate>Phone number</label>
        <md-input
          tabindex="7"
          v-model="user.phoneNumber"
          v-mask="'(###)###-####'"
          name="phone"
          id="phone"
          :required="user.enablePhoneContact || user.enableSmsAlerts"
        ></md-input>
        <font-awesome-icon
          :icon="['fa', 'check-circle']"
          class="check"
          :style="{ fontSize: '24px' }"
          v-if="user.validatedNumber"
        ></font-awesome-icon>
        <span class="profile__error" :class="['md-error', 'ruby-font']">
          {{ errors && errors.phoneNumber ? errors.phoneNumber[0] : '' }}
        </span>
      </md-field>
      <div v-if="enableSms" class="profile__section">
        <div>
          <label for="contact">
            <span v-translate class="profile__subtitle"
              >Can we reach out to you by phone if we need more information from you to process your
              grant request?</span
            >
            <span>&nbsp;</span>
          </label>
          <div></div>
        </div>
        <md-checkbox v-model="user.enablePhoneContact" :value="true">
          <p v-translate>Yes</p>
        </md-checkbox>
        <md-checkbox v-model="user.enablePhoneContact" :value="false">
          <p v-translate>No</p>
        </md-checkbox>
      </div>
    </form>
    <md-button :disabled="saving" class="button" @click="handleSubmit" v-if="!loading"
      ><span class="inherit-font" v-translate>Save</span>
    </md-button>
    <LoadSpinner v-if="saving || loading" />
  </div>
</template>

<script>
import Employee from '@/services/employees'
import User from '@/services/users'
import { objectToCamelCase } from '@/services/utils'
import LoadSpinner from '@/components/LoadSpinner'
import Organization from '@/services/organizations'

export default {
  name: 'MyProfile',
  components: {
    LoadSpinner,
  },
  data() {
    return {
      user: null,
      userId: '',
      loading: false,
      saving: false,
      errors: null,
      enableLangPref: this.$store.state.organizationDetails.enableLanguagePref,
      enableSms: true,
      zipCodeMask: '#####',
      clientErrors: {},
      employeeSchema: {},
    }
  },
  async created() {
    this.loading = true
    this.userId = this.$store.state.user.id
    await Employee.api.retrieve(this.userId).then((res) => {
      this.user = res
    })
    await this.getEmployeeSchema()
    this.loading = false
  },
  methods: {
    async getEmployeeSchema() {
      await Employee.api.schema().then((res) => {
        this.employeeSchema = res['actions']['POST']
      })
    },
    checkValidation() {
      const isZipCodeRequiredOnOrg = this.employeeSchema && this.employeeSchema.hasOwnProperty('zipCode')
      const newErrors = {}
      if (!this.user.firstName.length) {
        newErrors.firstName = [this.$gettext('First name may not be blank')]
      }
      if (!this.user.lastName.length) {
        newErrors.lastName = [this.$gettext('Last name may not be blank')]
      }
      if (!this.user.email.length) {
        newErrors.email = [this.$gettext('Email address may not be blank')]
      }
      if (!this.user.email.includes('@') || !this.user.email.includes('.')) {
        const msg = this.$gettext('Please provide a valid email address')
        newErrors.email ? newErrors.email.push(msg) : (newErrors.email = [msg])
      }
      if (this.user.confirmEmail !== this.user.email) {
        const msg = this.$gettext('Must match')
        newErrors.confirmEmail ? newErrors.confirmEmail.push(msg) : (newErrors.confirmEmail = [msg])
      }
      if (
        this.user.paypalEmail &&
        (!this.user.paypalEmail.includes('@') || !this.user.paypalEmail.includes('.'))
      ) {
        newErrors.paypalEmail = [this.$gettext('Please provide a valid email address')]
      }
      if (isZipCodeRequiredOnOrg && !this.user.zipCode) {
        newErrors.zipCode = [this.$gettext('Zip code may not be blank')]
      }
      if (isZipCodeRequiredOnOrg && !this.zipCodeValid) {
        const msg = this.$gettext('Please provide a valid zip code')
        newErrors.zipCode ? newErrors.zipCode.push(msg) : (newErrors.zipCode = [msg])
      }
      if (
        (this.user.enablePhoneContact || this.user.enableSmsAlerts) &&
        !this.user.phoneNumber.length
      ) {
        newErrors.phoneNumber = [this.$gettext('Phone number may not be blank')]
      }
      if (this.user.phoneNumber.length > 13) {
        const msg = this.$gettext('Please provide a valid phone number')
        newErrors.phoneNumber ? newErrors.phoneNumber.push(msg) : (newErrors.phoneNumber = [msg])
      }

      if (!this.user.preferredLanguage) {
        newErrors.preferredLanguage = [this.$gettext('Please select your preferred language')]
      }

      if (!this.user.enableSmsAlerts && !this.user.enableEmailAlerts) {
        newErrors.notifPreferences = [
          this.$gettext('Please select at least one of the options available'),
        ]
      }

      this.errors = newErrors
    },
    async handleSubmit() {
      this.checkValidation()
      const hasErrors = this.errors && Object.keys(this.errors) && Object.keys(this.errors).length
      if (this.saving || hasErrors) {
        return
      }
      this.saving = true
      this.errors = null
      this.user.hasSetNotifPreferences =
        !!this.user.enableEmailAlerts || !!this.user.enableSmsAlerts

      await Employee.api
        .update(this.user)
        .then((res) => {
          this.user = res
          this.$store.dispatch('updateUser', Employee.fromAPI(res))
          this.$store.commit('UPDATE_CURRENT_LANGUAGE', this.user.preferredLanguage)

          if (
            this.user.phoneNumber.length &&
            !this.user.validatedNumber &&
            this.user.enableSmsAlerts
          ) {
            this.sendCode(this.user)
          }
        })
        .catch((err) => {
          this.user.id = this.userId
          this.errors = objectToCamelCase(err.response.data)
        })
      this.saving = false
    },
    async sendCode(user) {
      try {
        const response = await User.api.twilioChallenge(user)
        this.$router.push({
          name: 'Validation',
          params: { routeName: 'Home' },
        })
      } catch (err) {
        this.$router.push({
          name: 'Validation',
          params: {
            codeError: true,
            errorMessage: err?.response?.data?.phone_number[0],
            routeName: 'MyProfile',
          },
        })
      }
    },
  },
  watch: {
    '$language.current': async function () {
      /* TODO: Move this to a more general area.*/
      await Organization.api.getMyOrganization().then(async (organization) => {
        this.$store.dispatch('updateOrganizationDetails', organization)
      })
    },
  },
  computed: {
    formattedDate() {
      const locale = this.$store.state.user.preferredLanguage.replace('_', '-') || 'en-US'
      return new Date().toLocaleDateString(locale)
    },
    phoneNumberValid() {
      if (!this.enableSms) {
        return true
      }
      if (this.user.phoneNumber.length === 0) {
        return true
      }
      if (this.user.phoneNumber.length === 13) {
        return true
      }
      return false
    },
    zipCodeValid() {
      if (!this.employeeSchema.hasOwnProperty('zipCode') && !this.user.zipCode){
        return true;
      }
      const zipRegex = new RegExp(/^[0-9]{5}?$/)
      this.errors = { ...this.errors, zipCode: [] }

      if (this.user.zipCode && this.user.zipCode.length === 0) {
        return true
      }

      const zipValid =
        this.user.zipCode &&
        this.user.zipCode.length > 1 &&
        (this.user.zipCode.length === 5 || this.user.zipCode.length === 10) &&
        !(this.errors && this.errors.zipCode && this.errors.zipCode.length > 0) &&
        zipRegex.test(this.user.zipCode)

      if (!zipValid) {
        this.clientErrors = {
          ...this.clientErrors,
          zipCode: [this.$gettext('Please provide a valid zip code')],
        }

        return false
      } else {
        this.clientErrors = { ...this.clientErrors, zipCode: [] }

        return true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';

.profile {
  @include screen-container();
  justify-content: unset;

  &__section {
    margin-bottom: 0.5em;
  }

  &__errors {
    font-size: 12px;
  }

  &__subtitle {
    font-size: 12px;
    font-weight: 300;
    line-height: 1rem;
  }
}
.button {
  @include primary-button(var(--dark-brand-color));
  margin-top: 2rem;
}

.check {
  color: var(--dark-brand-color);
}
.italics {
  font-style: italic;
}

.subheading {
  padding-bottom: 0.25em;
}

.line-spacing {
  margin: 1em 0 2em;
}

.form {
  width: 100%;
}

.ruby-font {
  color: $ruby;
}

.md-checkbox {
  margin-top: 8px;
}
</style>
